/**
 * Alpine component for the ultra-legacy yellow member-nav bar.
 *
 * Responsibility:
 *  - Observe the bar's container width.
 *  - On desktop (>= 640px), hide trailing items from the end until the bar fits.
 *  - Feed the hidden items into `window._yellowBarOverflow` so the "More"
 *    dropdown / mobile bottom sheet can render them.
 *
 * Usage in Twig (header/ultra-legacy/member_nav.twig):
 *     <div x-data="yellowBarCollapse()" x-ref="navBar">
 *         {# member links, each item with [data-collapsible] #}
 *     </div>
 *
 * Registration in `alpine-init.js`:
 *     import { yellowBarCollapse } from './alpine-yellow-bar.js';
 *     Alpine.data('yellowBarCollapse', yellowBarCollapse);
 *
 * Registering synchronously from `alpine-init.js` (rather than an inline
 * `document.addEventListener('alpine:init', ...)` callback in Twig) avoids a
 * race where Alpine's `alpine:init` event has already fired by the time the
 * inline listener attaches. The race caused Sentry LEGACY-R5RA / R5NQ / R58X
 * (`ReferenceError: yellowBarCollapse is not defined`).
 */

// Shared overflow buffer for the "More" dropdown and mobile bottom sheet. The
// Twig markup reads `window._yellowBarOverflow.populate(...)` from `x-effect`
// bindings, so this assignment is part of the component's public surface.
window._yellowBarOverflow = window._yellowBarOverflow || {
    items: [],
    populate(container) {
        container.innerHTML = '';
        if (!this.items.length) return;
        this.items.forEach((item) => {
            const a = document.createElement('a');
            a.href = item.href;
            a.className = 'flex items-center px-4 py-3 text-[15px] text-gray-900 hover:bg-slate-50 transition-colors duration-150';
            a.setAttribute('role', 'menuitem');
            if (item.iconBgPos) {
                const icon = document.createElement('div');
                icon.className = 'yb_cont mr-3 flex-shrink-0';
                icon.style.backgroundPosition = item.iconBgPos;
                a.appendChild(icon);
            }
            const span = document.createElement('span');
            span.textContent = item.label;
            a.appendChild(span);
            container.appendChild(a);
        });
        const divider = document.createElement('div');
        divider.className = 'h-px bg-gray-200 my-1';
        container.appendChild(divider);
    },
};

export function yellowBarCollapse() {
    return {
        _ro: null,

        init() {
            const bar = this.$refs.navBar;
            const container = bar.parentElement;
            const collapsibles = [...bar.querySelectorAll('[data-collapsible]')];
            if (!collapsibles.length) return;

            let scheduled = false;
            const recalc = () => {
                scheduled = false;
                // Reset visibility so width measurements reflect the full bar.
                collapsibles.forEach((el) => el.style.setProperty('display', '', ''));
                window._yellowBarOverflow.items = [];

                // On mobile (< 640px), keep all items visible and let the bar scroll.
                if (window.innerWidth < 640) return;

                // Use the bar's own rendered width as the constraint. The bar has
                // sm:max-w-[100vw], so clientWidth is capped at viewport width.
                const maxWidth = bar.clientWidth;
                if (maxWidth <= 0) return;

                // Hide items from the end until content fits.
                for (let i = collapsibles.length - 1; i >= 0; i--) {
                    if (bar.scrollWidth <= maxWidth) break;
                    const iconEl = collapsibles[i].querySelector('.yb_cont');
                    const iconBgPos = iconEl ? getComputedStyle(iconEl).backgroundPosition : null;
                    collapsibles[i].style.setProperty('display', 'none', 'important');
                    window._yellowBarOverflow.items.unshift({
                        href: collapsibles[i].getAttribute('href'),
                        label: collapsibles[i].getAttribute('data-label'),
                        iconBgPos,
                    });
                }
            };

            const scheduleRecalc = () => {
                if (!scheduled) {
                    scheduled = true;
                    requestAnimationFrame(recalc);
                }
            };

            // Observe the stable parent container to avoid ResizeObserver feedback
            // loops (hiding/showing items changes bar size, not container).
            this._ro = new ResizeObserver(scheduleRecalc);
            this._ro.observe(container);
            recalc();
        },

        destroy() {
            if (this._ro) this._ro.disconnect();
        },
    };
}
